import { PHASE_FIRMWARE, PHASE_HEALTH_FAULTS } from '../../../../constants';

import { VerticalContainer } from '@american-autonomy/ag-ui';

import PanelSection from 'components/PanelSection';

import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import ComponentRow from './components/ComponentRow';
import { PACK_VOLTAGE, RADAR_SELF_TEST, TEST_ID } from './constants';
import { getUnitRows } from './helpers/getUnitRows';
import type { FunctionComponent, Props, RowData } from './types';

const TabPanelComponents: FunctionComponent<Props> = ({
    className = '',
    drone,
    phase,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const okStatus = t.COMPONENT_STATUS_OK;
    const fwLabel = (status: string) =>
        status === 'compliant' ? t.FIRMWARE_COMPLIANT : t.FIRMWARE_OUTDATED;
    const { flowMeterRows, motorRows, pumpRows } = getUnitRows({
        drone,
        phase,
        serviceIntervals,
        strings: {
            drift: t.COMPONENT_STATUS_DRIFT,
            due: t.COMPONENT_STATUS_DUE,
            flowMeterLabel: t.PART_FLOW_METER,
            motorLabel: t.PART_MOTOR,
            ok: okStatus,
            overdue: t.SERVICE_OVERDUE,
            overtemp: t.COMPONENT_STATUS_OVERTEMP,
            pumpLabel: t.PUMP_ROW_LABEL,
            toServiceSuffix: t.TO_SERVICE_SUFFIX,
            unitLabels: {
                cycles: t.CYCLES_UNIT,
                flights: t.UNIT_FLIGHTS_LOWER,
                hours: t.UNIT_HOURS_SHORT
            }
        }
    });
    const batteryRows: RowData[] = [
        {
            label: 'Pack A',
            status: okStatus,
            value: `${drone.usage.battery ?? 0} cycles · ${PACK_VOLTAGE}`
        }
    ];
    const radarRows: RowData[] = [
        { label: t.RADAR_FRONT, status: okStatus, value: RADAR_SELF_TEST },
        { label: t.RADAR_BACK, status: okStatus, value: RADAR_SELF_TEST },
        { label: t.RADAR_DOWNWARD_TERRAIN, status: okStatus, value: RADAR_SELF_TEST }
    ];
    const firmwareRows: RowData[] = [
        { label: 'FC', status: fwLabel(drone.firmware.fc), value: '' },
        { label: t.PART_PUMP_SEAL_KIT, status: fwLabel(drone.firmware.pump), value: '' },
        { label: t.PART_ESC, status: t.NOT_APPLICABLE_NOT_REPORTED, value: '' },
        { label: t.PART_BATTERY, status: t.NOT_APPLICABLE_NOT_REPORTED, value: '' }
    ];
    const isLive = phase >= PHASE_HEALTH_FAULTS;
    const unitSections: Array<{ rows: RowData[]; text: string }> = [
        { rows: motorRows, text: t.SECTION_MOTORS },
        { rows: pumpRows, text: t.SECTION_PUMPS },
        { rows: flowMeterRows, text: t.SECTION_FLOW_METER }
    ];
    const liveSections: Array<{ rows: RowData[]; text: string }> = isLive
        ? [
              { rows: batteryRows, text: t.SECTION_BATTERY_PACKS },
              { rows: radarRows, text: t.SECTION_RADARS }
          ]
        : [];
    const firmwareSections: Array<{ rows: RowData[]; text: string }> =
        phase >= PHASE_FIRMWARE ? [{ rows: firmwareRows, text: t.SECTION_FIRMWARE }] : [];
    const sections: Array<{ rows: RowData[]; text: string }> = [
        ...unitSections,
        ...liveSections,
        ...firmwareSections
    ];

    return (
        <VerticalContainer className={className} testId={testId}>
            {sections.map(section => (
                <PanelSection key={section.text} text={section.text}>
                    {section.rows.map(row => (
                        <ComponentRow
                            key={`${section.text}-${row.label}`}
                            label={row.label}
                            status={row.status}
                            value={row.value}
                        />
                    ))}
                </PanelSection>
            ))}
        </VerticalContainer>
    );
};

export default TabPanelComponents;
